import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useConfig } from '@ohpen/react-utils';
import { useApiClient, UseApiClientOptions, useAuth } from '@ohpen/react-auth';
import { PageSpinner, NotFound } from '@ohpen/enso-ui';

import * as pmpSdk from '@pmp/api';
import { AppLayout, ConfigHost, VersionBanner } from '@pmp/components';
import { AuthzProvider, HostChromeProvider, PmpConfigProvider } from '@pmp/contexts';
import { useFeatureToggles } from '@pmp/hooks';
import { Router } from '../../router';
import { registerAssistantAuth, registerAssistantAgentUrl, PmpAssistantAppConfig } from '../../assistant/agentTransport';
import { AssistantGate } from '../../assistant/AssistantGate';

/**
 * Configuration replaces the whole shell rather than rendering inside PMP's, because it
 * carries its own top bar and side bar and will one day be its own deployment.
 */
const AuthenticatedRoutes = (): ReactElement => {
  const featureToggles = useFeatureToggles();

  // Undefined until the configuration endpoint answers. Registering the routes before then makes
  // the catch-all win, so a reload straight onto /config lands on PMP's not-found page.
  if (!featureToggles) return <PageSpinner />;

  return (
    <Routes>
      {featureToggles.enableConfig && <Route path="config/*" element={<ConfigHost />} />}
      <Route
        path="*"
        element={
          <AppLayout>
            <Router />
          </AppLayout>
        }
      />
    </Routes>
  );
};

export const AuthenticatedContent = () => {
  const config = useConfig<PmpAssistantAppConfig>();
  const auth = useAuth();
  const [showBanner, setShowBanner] = useState(false);
  const versionErrorShown = useRef(false);
  const agentUrl = config.APP?.agentUrl;

  // Give the Assistant transport (which lives above AuthProvider) the session token getter
  // and the agent's per-environment URL (config.json APP.agentUrl; see agentTransport.ts).
  useEffect(() => {
    registerAssistantAuth(auth.getAccessToken);
    registerAssistantAgentUrl(agentUrl);
  }, [auth.getAccessToken, agentUrl]);

  const reload = useCallback(() => {
    if ('caches' in globalThis) {
      caches.keys().then(names => Promise.all(names.map(name => caches.delete(name))));
    }

    const url = new URL(globalThis.location.href);
    url.searchParams.set('_', Date.now().toString());
    globalThis.location.href = url.toString();
  }, []);

  const dismissBanner = useCallback(() => setShowBanner(false), []);

  const onVersionError = useCallback(() => {
    if (versionErrorShown.current) return;
    versionErrorShown.current = true;
    setShowBanner(true);
  }, []);

  const apiClientOptions: UseApiClientOptions = {
    config,
    version: config.VERSION,
    onVersionError,
    baseUrl: config?.APP?.apiBaseurl ?? '',
    client: pmpSdk.client,
    sdkModule: pmpSdk,
    getAccessToken: auth.getAccessToken,
  };

  const { isReady: apiClientReady, error: apiClientError } = useApiClient(apiClientOptions);

  if (!apiClientReady) {
    return <PageSpinner />;
  }

  if (apiClientError) {
    return <NotFound />;
  }

  return (
    <PmpConfigProvider getAccessToken={auth.getAccessToken}>
      <AuthzProvider>
        <HostChromeProvider>
          <AssistantGate />
          <AuthenticatedRoutes />
        </HostChromeProvider>
      </AuthzProvider>
      {showBanner && <VersionBanner onReload={reload} onDismiss={dismissBanner} />}
    </PmpConfigProvider>
  );
};
